import { IconPhotoOutline18 as ImagePlayIcon } from "nucleo-ui-outline-18"

import type { CustomBodyParams, SlashCommandDefinition } from "../types"
import type { api } from "@/lib/api"
import { ALLOWED_FILE_SIZE_IN_MB } from "@/constants"
import { processCoverImage } from "@/lib/process-cover-image"
import { iconClassName } from "../constants"

type MediaCustomProps = CustomBodyParams & {
	url?: string
	type?: "image" | "video"
}

export const uploadMediaFile = async (
	file: File,
	getSignedUrlMutation: ReturnType<typeof api.file.getUrl.useMutation>,
	// A lone cover image gets the padded plate baked in; gallery slides keep
	// their own pixels, so the carousel shows the screenshot itself.
	{ processImage = true }: { processImage?: boolean } = {},
): Promise<{
	url: string
	type: "image" | "video"
	originalUrl?: string
}> => {
	const isImage = file.type.startsWith("image")

	let fileToUpload = file
	let originalUrl: string | undefined

	if (isImage && processImage) {
		fileToUpload = await processCoverImage(file)

		const originalFileSize = (file.size / 1024 / 1024).toFixed(4)
		if (Number(originalFileSize) < ALLOWED_FILE_SIZE_IN_MB) {
			const { uploadUrl: originalUploadUrl, fileUrl: originalFileUrl } =
				await getSignedUrlMutation.mutateAsync({
					fileName: `original_${file.name}`,
					fileType: file.type,
				})

			const originalHeaders = { "Content-Type": file.type }
			const originalRes = await fetch(originalUploadUrl, {
				method: "PUT",
				body: file,
				headers: originalHeaders,
			})
			if (originalRes.ok) {
				originalUrl = originalFileUrl
			}
		}
	}

	const { uploadUrl, fileUrl } = await getSignedUrlMutation.mutateAsync({
		fileName: fileToUpload.name,
		fileType: fileToUpload.type,
	})
	const filesize = (fileToUpload.size / 1024 / 1024).toFixed(4)
	if (Number(filesize) >= ALLOWED_FILE_SIZE_IN_MB) {
		throw new Error(
			`Media files need to be less than ${ALLOWED_FILE_SIZE_IN_MB}mb in size.`,
		)
	}

	const headers = {
		"Content-Type": fileToUpload.type,
	}
	const res = await fetch(uploadUrl, {
		method: "PUT",
		body: fileToUpload,
		headers,
	})
	if (!res.ok) {
		throw new Error("File upload failed")
	}

	const type = isImage ? "image" : "video"
	return { url: fileUrl, type, originalUrl }
}

export const mediaCommand: SlashCommandDefinition<MediaCustomProps> = {
	key: "media",
	title: "Media",
	aliases: ["image", "video", "pdf"],
	icon: (
		<>
			<ImagePlayIcon strokeWidth={1.75} className={iconClassName} />
		</>
	),
	command: ({ editor, range }) => {
		editor.chain().focus().deleteRange(range).run()

		// Capture insertion position now; the file picker is async and the
		// editor selection is lost by the time the upload resolves.
		const insertPos = editor.state.selection.from

		window.dispatchEvent(
			new CustomEvent("media-upload-request", {
				detail: { editor, insertPos },
			}),
		)
	},
	group: "media",
}
